import React, { useEffect, useState } from "react";
import {
  Address,
  AddressLookup,
  AddressResult,
  ConnectionAddressObject,
  constructDisplayLabel,
  getSapIdFromAddress,
  qasLookup,
  QasProfile,
} from "@origin-digital/address-lookup";
import { IConnectionAddress } from "../../types";
import { errorForm, logger } from "@origin-digital/reporting-client";
import { createGlobalStyle } from "styled-components";
import { Box, Stack, Text, TextLink } from "@origin-digital/ods-core";
import content from "./content.json";
import { IContext, useAppContext } from "../../context/AppContext";
import { submitInteractionEvent } from "../../utils/analytics";
import { APP_NAME } from "../../consts";
import { AddressSelectorHelperText } from "./AddressSelectorHelperText";

/**
 * This class wraps the ConnectionAddress from @origin-digital/address-lookup.
 * Providing all the common props this application need to supply when using the ConnectionAddress
 * component.
 */
export interface IAddressLookupWrapper {
  connectionAddress: ConnectionAddressObject | undefined;
  label: string;
  allowManualAddressEntry?: boolean;
  helpText?: string;
  qasProfile?: QasProfile;
  errorMessage?: string;
  setErrorMessage: (error?: string) => void;
  onNaturalGasDetected?: (originAddressId: string) => void;
  onAddressReset?: () => void;
}

// The MUI Autocomplete popper is portaled to document.body so parent-scoped CSS
// cannot reach it. createGlobalStyle injects into <head> and targets the popper globally.
const AddressLookupPopperFix = createGlobalStyle`
  @media screen and (max-width: 480px) {
    .MuiAutocomplete-popper [data-id="text"] {
      font-size: 12px !important;
    }
    .MuiAutocomplete-popper button#autocomplete-manual-address-trigger,
    .MuiAutocomplete-popper button#autocomplete-manual-address-trigger span {
      font-size: 12px !important;
      padding-left: 4px !important;
      padding-right: 4px !important;
    }
  }
`;

export enum AddressSelectorErrorType {
  FETCH_ERROR_ADDRESS_SUGGESTIONS = "FETCH_ERROR_ADDRESS_SUGGESTIONS",
  FETCH_ERROR_STRUCTURED_ADDRESS = "FETCH_ERROR_STRUCTURED_ADDRESS",
  FETCH_ERROR_ORIGIN_ADDRESS_ID = "FETCH_ERROR_ORIGIN_ADDRESS_ID",
  VALIDATION_ERROR_BLANK_ADDRESS = "VALIDATION_ERROR_BLANK_ADDRESS",
  VALIDATION_ADDRESS_SUGGESTION_NOT_SELECTED = "VALIDATION_ADDRESS_SUGGESTION_NOT_SELECTED",
}

/**
 * AddressSelector lifecycle
 * 1. User types in address
 * 2. Each key press triggers _onQueryChange()
 * 3. _onQueryChange() updates userEnteredAddressInput with user input
 * 4. AddressLookup internally calls _fetchAddresses() on each user input update after 3 chars
 * 5. _fetchAddresses() calls QAS API to get address suggestions for user input (not userEnteredAddressInput but internal AddressLookup value)
 * 6. User selects an address from the suggestions
 * 7. AddressLookup internally calls _onSelect()
 * 8. _onSelect() calls _getAddressDetailsAndSetForPlansFetch()
 * 9. _getAddressDetailsAndSetForPlansFetch() calls fetchAddressDetails() to get structured address details
 * 10. _getAddressDetailsAndSetForPlansFetch() calls validateAndUpdateAddress() to get originAddressId and meter details
 * 11. validateAndUpdateAddress() calls _onChange() with the ConnectionAddressObject (originaddressId/meter details) and sets addressLookup and context
 **/

export const AddressSelector = ({
  connectionAddress,
  label,
  allowManualAddressEntry,
  helpText,
  qasProfile,
  errorMessage,
  setErrorMessage,
  onNaturalGasDetected,
  onAddressReset,
}: IAddressLookupWrapper) => {
  const { setContext } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [addressErrorEventFired, setAddressErrorEventFired] = useState<
    Partial<Record<AddressSelectorErrorType, boolean>>
  >({});
  const [manualAddressMode, setManualAddressMode] = useState<boolean>(false);
  const [manualAddressErrorMessage, setManualAddressErrorMessage] =
    useState("");
  const [userEnteredAddressInput, setUserEnteredAddressInput] = useState("");
  const [lookupAddress, setLookupAddress] = useState<ConnectionAddressObject>();
  const [, setHasNaturalGas] = useState(false);

  const updateContextWithAddressSelection = (
    qasAddress?: ConnectionAddressObject,
    extraContext?: Partial<IContext>
  ) => {
    // officially take user selection when qas returns a valid address or
    // when user finalises manual address selection
    const addressSelection = qasAddress || lookupAddress;
    // update addressSelection with origin address id if it's a manual address
    if (addressSelection && qasAddress) {
      addressSelection.originAddressId = qasAddress.originAddressId;
    }
    logger.debug(
      "[AddressSelector.updateContextWithAddressSelection] Set app context",
      { addressSelection }
    );
    if (addressSelection?.originAddressId) {
      submitInteractionEvent(
        "click",
        "button",
        "lpg-plans",
        APP_NAME,
        "filter-address"
      );
      setContext({
        lookupAddress: addressSelection,
        loading: true,
        ...extraContext,
      });
    }
    // TO-1476: temporarily stop scroll until better solution is found
    // we scroll to show users at the bottom of the page that offers changes
    // but users who are already at offers (most likely scenario) will be disorientated
    // window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Initiate `fetchAddresses` method with configuration
  const { fetchAddresses } = qasLookup(
    TAL.config.addressSelector.qasAuthToken,
    qasProfile
  );

  // Initiate `fetchAddressDetails` method with configuration
  const { fetchAddressDetails } = qasLookup(
    TAL.config.addressSelector.qasAuthToken
  );

  const getQueryValue = (address: ConnectionAddressObject): string => {
    if (address && address.qasSearchResult && address.qasSearchResult.label) {
      return address.qasSearchResult.label;
    } else if (address.qasAddress) {
      if (address.isManualAddress) {
        // Address coming back from the manual input components sets `unitType` and `unitNumber` whereas contructDisplayLabel wants `unit`
        address.qasAddress.unit = `${address.qasAddress.unitType || ""} ${
          address.qasAddress.unitNumber || ""
        }`.trim();
      }
      return constructDisplayLabel({ ...address.qasAddress });
    }
    return "";
  };

  const setManualAddressModeAndScrollTopIfRequired = () => {
    const hasManualModeComponents = !!document.querySelector(
      "[data-id='connectionAddress-manual-entry']"
    );
    if (manualAddressMode !== hasManualModeComponents) {
      // TO-1476: temporarily stop scroll until better solution is found
      // we scroll to show users at the bottom of the page that offers changes
      // but users who are already at offers (most likely scenario) will be disorientated
      // window.scrollTo({ top: 0, behavior: "smooth" });
      setManualAddressMode(hasManualModeComponents);
    }
  };

  /**
   * When a pre-loaded ConnectionAddress is available fill the
   * address component with that. Also, pass that connection address
   * to `onChange` callback to load plans
   */
  useEffect(() => {
    const connAddress = connectionAddress as IConnectionAddress;

    if (connAddress && connAddress.qasAddress) {
      setUserEnteredAddressInput(getQueryValue(connAddress));
      updateContextWithAddressSelection(connAddress);
      if (
        connAddress.mirnStatus === "FOUND" &&
        connAddress.qasAddress.isExactMatchForMIRN
      ) {
        setHasNaturalGas(true);
        onNaturalGasDetected?.(connAddress.originAddressId ?? "");
      }
    }
  }, [connectionAddress]);

  /**
   * Resets progress indicator & error messages
   */
  const _resetProgressIndicators = () => {
    setLoading(false);
    setError(false);
    setSuccess(false);
    setErrorMessage(undefined);
    setManualAddressErrorMessage("");
  };

  const _errorMessageFromType = (type: AddressSelectorErrorType) => {
    switch (type) {
      case AddressSelectorErrorType.VALIDATION_ERROR_BLANK_ADDRESS:
        return "Please enter your address";
      case AddressSelectorErrorType.VALIDATION_ADDRESS_SUGGESTION_NOT_SELECTED:
        return "Oops, that address doesn't look right";
      case AddressSelectorErrorType.FETCH_ERROR_ADDRESS_SUGGESTIONS:
      case AddressSelectorErrorType.FETCH_ERROR_STRUCTURED_ADDRESS:
      case AddressSelectorErrorType.FETCH_ERROR_ORIGIN_ADDRESS_ID:
      default:
        return content.invalidAddress;
    }
  };

  /**
   * Called when an error occurs with address search
   *
   * @param type AddressSelectorFetchType
   */
  const _onError = (type: AddressSelectorErrorType) => {
    const message = _errorMessageFromType(type);
    setErrorMessage(message);
    if (!addressErrorEventFired[type]) {
      errorForm({
        appName: APP_NAME,
        formName: "lpg-address-selector",
        errorMessage: message,
        errorCode: type,
      });
      setAddressErrorEventFired({
        ...addressErrorEventFired,
        [type]: true,
      });
    }
  };

  /**
   * Checks validity of address search input
   */
  const _validate = (): void => {
    if (loading) {
      return;
    }

    _resetProgressIndicators();

    if (lookupAddress) {
      setSuccess(true);
    } else {
      const validationErrorType =
        userEnteredAddressInput?.length === 0
          ? AddressSelectorErrorType.VALIDATION_ERROR_BLANK_ADDRESS
          : AddressSelectorErrorType.VALIDATION_ADDRESS_SUGGESTION_NOT_SELECTED;
      setError(true);
      _onError(validationErrorType);
    }
  };

  /**
   * Called when user focus away from address input.
   * This will validate the input string and show error message if appropriate
   */
  const _onBlur = (): void => {
    setManualAddressModeAndScrollTopIfRequired();
    _validate();
  };

  /**
   * Accepts a QAS address and pass into call back function
   *
   * @param address QAS address
   */
  const _onChange = (
    address: ConnectionAddressObject | undefined,
    extraContext?: Partial<IContext>
  ): void => {
    setManualAddressModeAndScrollTopIfRequired();
    setError(false); // clear validation error on any change
    setErrorMessage(undefined); // clear validation error on any change
    setHasNaturalGas(false);
    onAddressReset?.();
    if (address) {
      logger.debug("[AddressSelector.handleChange] Handle address change", {
        address,
      });
      if (address.isManualAddress) {
        // manual address will change any time a field is updated
        setLookupAddress(address);
        if (address.originAddressId) {
          updateContextWithAddressSelection(address, extraContext);
        }
      } else {
        // nonmanual address only after qas returns
        setLookupAddress(address);
        updateContextWithAddressSelection(address, extraContext);
      }
      setSuccess(true);
    } else {
      // nonmanual address will change after field is updated
      // should clear any previously selected address
      setLookupAddress(undefined);
    }
  };

  /**
   * Called when address input value changes
   *
   * @param newValue New address query
   */
  const _onQueryChange = (newValue: string) => {
    _resetProgressIndicators();

    setUserEnteredAddressInput(newValue);

    // Reset context address value
    _onChange(undefined);
  };

  /**
   * Build the base params object for the SAP address lookup.
   */
  const _buildAddressParams = (address: Address) => ({
    dpid: null,
    streetNumber: address.streetNumber,
    streetName: address.streetName,
    streetType: address.streetType,
    city: address.city,
    state: address.state,
    postcode: address.postcode,
    unit: address.unit
      ? address.unit
      : [address.unitType, address.unitNumber].filter(Boolean).join(" "),
    unitType: address.unitType,
    unitNumber: address.unitNumber,
    level: address?.level,
    resolveAddress: true,
    includeType2Discovery: true,
  });

  /**
   * Validate QAS address against SAP — primary call (full meter resolution).
   * Waits for API/client completion and only falls back if this call fails.
   *
   * @param address QAS Address
   * @returns Promise<AddressTranslationAPIResponse>
   */
  const _validateAddress = (address: Address): Promise<any> => {
    const params = _buildAddressParams(address);

    return getSapIdFromAddress(
      TAL.config.addressSelector.addressTranslationApiEndPoint,
      TAL.config.addressSelector.addressTranslationApiKey,
      params
    );
  };

  /**
   * Fallback call to SAP using asyncMeterResolution=true.
   * Returns quickly with only originAddressId — no NMI/MIRN data.
   * Used when the primary call times out or fails during a Kraken outage.
   *
   * @param address QAS Address
   * @returns Promise<AddressTranslationAPIResponse>
   */
  const _validateAddressFallback = (address: Address): Promise<any> => {
    const params = {
      ..._buildAddressParams(address),
      asyncMeterResolution: true,
    };
    return getSapIdFromAddress(
      TAL.config.addressSelector.addressTranslationApiEndPoint,
      TAL.config.addressSelector.addressTranslationApiKey,
      params
    );
  };

  const validateAndUpdateAddress = async (
    address: Address,
    isManual: boolean,
    option?: AddressResult
  ) => {
    const applyValidatedAddress = (
      sapValidatedAddress: any,
      krakenOutage: boolean
    ) => {
      try {
        const { mirnStatus, isExactMatchForMIRN } = sapValidatedAddress.data;

        if (!krakenOutage) {
          logger.info(
            `"${option?.label}" address returned mirnStatus ${mirnStatus} and isExactMatchForMIRN ${isExactMatchForMIRN}`
          );
        }

        _onChange(
          {
            qasAddress: {
              ...address,
              isExactMatchForMIRN: krakenOutage
                ? undefined
                : isExactMatchForMIRN,
            },
            qasSearchResult: option,
            isManualAddress: isManual,
            nmi: krakenOutage ? undefined : sapValidatedAddress.data.nmi,
            nmiStatus: krakenOutage
              ? undefined
              : sapValidatedAddress.data.nmiStatus,
            elecDetails: krakenOutage
              ? undefined
              : sapValidatedAddress.data.elecDetails,
            mirn: krakenOutage ? undefined : sapValidatedAddress.data.mirn,
            mirnStatus: krakenOutage ? undefined : mirnStatus,
            isNMIOnSupply: krakenOutage
              ? undefined
              : sapValidatedAddress.data.isNMIOnSupply,
            isMIRNOnSupply: krakenOutage
              ? undefined
              : sapValidatedAddress.data.isMIRNOnSupply,
            originAddressId: sapValidatedAddress.data.originAddressId,
          },
          // Propagate outage state in the same setContext call as lookupAddress/loading
          // to avoid a stale-closure race where a second setContext call overwrites the first
          { krakenOutage }
        );

        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(true);
        _onError(AddressSelectorErrorType.FETCH_ERROR_ORIGIN_ADDRESS_ID);
      }
    };

    try {
      const sapValidatedAddress = await _validateAddress(address);
      applyValidatedAddress(sapValidatedAddress, false);
    } catch (primaryErr) {
      logger.warn(
        "[AddressSelector.validateAndUpdateAddress] Primary call failed - attempting fallback",
        { error: primaryErr }
      );

      // Fallback is intentionally non-awaited so primary failure path does not block this call stack.
      _validateAddressFallback(address)
        .then((sapValidatedAddress) => {
          logger.info(
            "[AddressSelector.validateAndUpdateAddress] Fallback call succeeded (Outage mode)"
          );
          applyValidatedAddress(sapValidatedAddress, true);
        })
        .catch((fallbackErr) => {
          logger.warn(
            "[AddressSelector.validateAndUpdateAddress] Fallback call failed",
            { error: fallbackErr }
          );
          // Both calls failed - show the hard error as before
          setLoading(false);
          setError(true);
          _onError(AddressSelectorErrorType.FETCH_ERROR_ORIGIN_ADDRESS_ID);
        });
    }
  };

  /**
   * Fetch details of address for given QAS address option
   * The method uses `fetchAddressDetails` function from address-lookup component
   *
   * @param option Address option
   */
  const _getAddressDetailsAndSetForPlansFetch = async (
    option: AddressResult
  ) => {
    setUserEnteredAddressInput(option.label);
    try {
      const address = await fetchAddressDetails(option.addressId);
      await validateAndUpdateAddress(address, false, option);
    } catch (err) {
      setLoading(false);
      setError(true);
      _onError(AddressSelectorErrorType.FETCH_ERROR_STRUCTURED_ADDRESS);
    }
  };

  /**
   * Called when an address option is selected from the list
   *
   * @param option selected address option
   */
  const _onSelect = async (option: AddressResult) => {
    if (!option) {
      return;
    }

    _resetProgressIndicators();
    setLoading(true);

    await _getAddressDetailsAndSetForPlansFetch(option);
  };

  /**
   * Fetch address options from QAS for address query
   * The method uses `fetchAddresses` function from address-lookup component
   *
   * @param query Address string to be searched for
   * @returns Promise<AddressResult[]>
   */
  const _fetchAddresses = async (query: string): Promise<AddressResult[]> => {
    try {
      const addresses = await fetchAddresses(query);

      // if connectionAddress is already provided, show green tick upon address fetch
      const connAddress = connectionAddress;
      if (addresses.length && connAddress && connAddress.qasAddress) {
        setSuccess(true);
      }

      return addresses;
    } catch (err) {
      setError(true);
      // Disabled, when called by internally by <AddressLookup/>
      // addressErrorEventFired state is reset for some reason (uncontrolled?)
      // causing any AddressSelectorFetchType error to be reported multiple times
      // _onFetchError(AddressSelectorFetchType.QAS_ADDRESS_SUGGESTIONS);
    }
    return [];
  };

  /**
   * Called when user changes a manual address field
   *
   * @param field
   * @param value
   * @param event
   */
  const _onManualChange = (): void => {
    setManualAddressModeAndScrollTopIfRequired();
  };

  /**
   * Address search for manual address values
   *
   * @param address Address to be searched for
   * @param errors Validation errors
   */
  const _onManualSubmit = async (address: any, errors: string[]) => {
    _resetProgressIndicators();

    setLoading(true);
    if (errors.length) {
      setLoading(false);
      return;
    }

    await validateAndUpdateAddress(address, true);
  };

  return (
    <>
      <AddressLookupPopperFix />

      <Box marginBottom="small">
        <Stack space={{ xs: "large", lg: "xlarge" }}>
          <Text>
            {content.reorderText}
            <TextLink href={content.reorderLinkHref}>
              {content.reorderLinkText}
            </TextLink>
          </Text>
          <AddressLookup
            id="address-lookup"
            data-id="connectionAddress"
            // @ts-ignore-next AddressLookup only supports string but underlying Autocomplete supports Element
            helperText={
              <AddressSelectorHelperText
                errorMessage={errorMessage}
                helpText={helpText}
              />
            }
            label={label}
            value={userEnteredAddressInput}
            forcePopupIcon={false}
            disableClearable={true}
            endIcon={
              success
                ? "success"
                : error
                ? "error"
                : loading
                ? "validating"
                : null
            }
            error={errorMessage !== undefined}
            manualEntry={allowManualAddressEntry}
            manualEntryProps={{
              id: "address-manual-entry",
              onSubmit: _onManualSubmit,
              onChange: _onManualChange,
              errorMessage: manualAddressErrorMessage,
            }}
            fetchAddresses={_fetchAddresses}
            onSelect={_onSelect}
            onQueryChange={_onQueryChange}
            onBlur={_onBlur}
          />
        </Stack>
      </Box>
    </>
  );
};
